// src/pages/DetalleVenta.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

import { db, auth } from "../firebaseConfig";
import "./DetalleVenta.css";

function formatDateAny(value) {
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString();
    }
    if (value instanceof Date) return value.toLocaleString();
    if (typeof value === "string") return value;
    return "—";
  } catch {
    return "—";
  }
}

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function DetalleVenta() {
  const navigate = useNavigate();
  const { idventa } = useParams(); // ruta: /ventas/:idventa  (ajústala si es diferente)

  const [uid, setUid] = useState(undefined);
  const [venta, setVenta] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // sesión
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
    });
    return () => unsub();
  }, []);

  // cargar venta
  useEffect(() => {
    if (!uid || !idventa) return;

    let alive = true;
    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const ref = doc(db, "usuarios", uid, "ventas", idventa);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          throw new Error("No se encontró la venta.");
        }

        const data = { id: snap.id, ...snap.data() };
        if (alive) setVenta(data);
      } catch (e) {
        console.error(e);
        if (alive) setErrorMsg(e?.message || "Error al cargar la venta");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [uid, idventa]);

  const productId = useMemo(() => {
    if (!venta) return "";
    return venta.productoId || venta.productId || venta.ptoductoId || "";
  }, [venta]);

  const total = useMemo(() => {
    const p = Number(venta?.precioUnitario ?? 0);
    const c = Number(venta?.cantidad ?? 0);
    if (Number.isNaN(p) || Number.isNaN(c)) return null;
    return p * c;
  }, [venta]);

  // UI estados
  if (uid === undefined) {
    return <div className="dvWrap">Cargando sesión…</div>;
  }

  if (uid === null) {
    return (
      <div className="dvWrap">
        <h2>Detalle de venta</h2>
        <p>Necesitas iniciar sesión.</p>
        <button className="btnPrimary" onClick={() => navigate("/login")}>
          Iniciar sesión
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="dvWrap">Cargando venta…</div>;
  }

  if (errorMsg) {
    return (
      <div className="dvWrap">
        <h2>Detalle de venta</h2>
        <div className="errorBox">{errorMsg}</div>
        <button className="btnSecondary" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  if (!venta) {
    return (
      <div className="dvWrap">
        <h2>Detalle de venta</h2>
        <div className="emptyBox">No hay datos.</div>
        <button className="btnSecondary" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="dvWrap">
      <div className="dvHeader">
        <div>
          <h2 className="dvTitle">Detalle de venta</h2>
          <div className="dvSub">
            Compra: <b>{formatDateAny(venta.fechaAgregado || venta.createdAt)}</b>
          </div>
        </div>

        <div className="dvHeaderBtns">
          <button className="btnSecondary" onClick={() => navigate(-1)}>
            Volver
          </button>

          <button
            className="btnPrimary"
            disabled={!productId}
            onClick={() => navigate(`/admin/productos/editar/${productId}`)}
            title={!productId ? "No hay productId en esta venta" : ""}
          >
            Ver producto
          </button>
        </div>
      </div>

      <div className="dvGrid">
        {/* Imagen */}
        <div className="dvCard dvImageCard">
          <div className="dvCardTitle">Imagen</div>
          {venta.imagenVariante ? (
            <img
              className="dvImage"
              src={venta.imagenVariante}
              alt={venta.tituloVariante || venta.titulo || "Imagen variante"}
              loading="lazy"
            />
          ) : (
            <div className="dvMuted">Sin imagenVariante</div>
          )}
        </div>

        {/* Datos principales */}
        <div className="dvCard">
          <div className="dvCardTitle">Producto</div>

          <div className="dvField">
            <div className="dvLabel">Título</div>
            <div className="dvValue">{venta.titulo || "—"}</div>
          </div>

          <div className="dvField">
            <div className="dvLabel">Variante</div>
            <div className="dvValue">{venta.tituloVariante || "—"}</div>
          </div>

          <div className="dvFieldRow">
            <div className="dvField">
              <div className="dvLabel">productoId</div>
              <div className="dvValue mono">{productId || "—"}</div>
            </div>

            <div className="dvField">
              <div className="dvLabel">idVariante</div>
              <div className="dvValue mono">{venta.idVariante || "—"}</div>
            </div>
          </div>
        </div>

        {/* Compra */}
        <div className="dvCard">
          <div className="dvCardTitle">Compra</div>

          <div className="dvField">
            <div className="dvLabel">Comprador (userName)</div>
            <div className="dvValue">{venta.userName || "—"}</div>
          </div>

          <div className="dvFieldRow">
            <div className="dvField">
              <div className="dvLabel">Precio unitario</div>
              <div className="dvValue">$ {money(venta.precioUnitario)}</div>
            </div>

            <div className="dvField">
              <div className="dvLabel">Cantidad</div>
              <div className="dvValue">{venta.cantidad ?? "—"}</div>
            </div>
          </div>

          <div className="dvDivider" />

          <div className="dvField">
            <div className="dvLabel">Total</div>
            <div className="dvValue dvTotal">
              {total == null ? "—" : `$ ${money(total)}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}